// src/utils/sourceTracking.js

/**
 * Identify which page brought the visitor in: 'homepage', 'theme:<slug>',
 * or 'blog:<slug>'. Used to attribute Mixpanel events - and eventually a
 * subscription - back to a specific landing page or blog post.
 */
export const getLandingPageIdentity = () => {
  const path = window.location.pathname;
  const themeMatch = path.match(/^\/theme\/([^/]+)/);
  if (themeMatch) {
    return `theme:${themeMatch[1]}`;
  }
  const blogMatch = path.match(/^\/blog\/([^/]+)/);
  if (blogMatch) {
    return `blog:${blogMatch[1]}`;
  }
  // The AI-drafted local-events pages linked from social posts' bio links
  // and suggested first comments (see app.py's _attach_bio_url /
  // _build_suggested_first_comment) - captured so a visitor who arrives via
  // one of those and later signs up shows up as landing_page=
  // "local-events:<city_slug>" rather than null, same as theme/blog pages.
  const localEventsMatch = path.match(/^\/local-events\/([^/]+)/);
  if (localEventsMatch) {
    return `local-events:${localEventsMatch[1]}`;
  }
  if (path === '/' || path === '') {
    return 'homepage';
  }
  return null;
};

const LANDING_PAGE_STORAGE_KEY = 'landing_page';

/**
 * First-touch capture of which landing page brought this visitor in.
 * Several CTAs (Home.js/ThemeLandingPage.js) are plain <a href> links that
 * cause a full page reload before signup/checkout, which would otherwise
 * lose whatever getLandingPageIdentity() saw on the very first pageview
 * (e.g. re-running it on /create-account resolves to null). Persisting to
 * localStorage - written once, never overwritten - keeps that first value
 * available for the rest of the visitor's device/browser, not just the
 * in-memory session, so it can be sent to the backend at signup/checkout.
 */
export const captureLandingPage = () => {
  const existing = localStorage.getItem(LANDING_PAGE_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const identity = getLandingPageIdentity();
  if (identity) {
    localStorage.setItem(LANDING_PAGE_STORAGE_KEY, identity);
  }
  return identity;
};

/** The landing page captured (if any) since this visitor's first pageview. */
export const getStoredLandingPage = () => localStorage.getItem(LANDING_PAGE_STORAGE_KEY);

/**
 * Explicitly record which theme the visitor picked from the Event Series
 * directory (/events) - unlike captureLandingPage() above, this overwrites
 * any first-touch value already stored (e.g. "homepage"). /events exists
 * specifically for visitors who didn't land on a specific theme page (see
 * EventSeries.js), so without this their eventual subscription would stay
 * attributed to "homepage" forever even though they explicitly chose a
 * theme - an explicit selection is a stronger signal than which page they
 * happened to land on first. Called from EventSeries.js's handleSelect,
 * before navigating to the theme page.
 */
export const recordThemeSelection = (slug) => {
  localStorage.setItem(LANDING_PAGE_STORAGE_KEY, `theme:${slug}`);
};

const BLOG_CTA_CLICK_STORAGE_KEY = 'blog_cta_clicked';

/**
 * Record that the visitor clicked a CTA inside a specific blog post, as
 * distinct from merely having viewed one (captured separately via
 * captureLandingPage() above). A click is a stronger, more deliberate
 * signal than a passive first-touch pageview, so - unlike
 * captureLandingPage()'s write-once behaviour - this always overwrites
 * with the most recent click, on the theory that the last CTA someone
 * actually clicked before signing up is more meaningful than the first
 * blog post they happened to land on. Read by Checkout.js and attached to
 * Stripe metadata alongside landing_page/landing_page_variant.
 */
export const recordBlogCtaClick = (slug) => {
  localStorage.setItem(BLOG_CTA_CLICK_STORAGE_KEY, slug);
};

export const getStoredBlogCtaClick = () => localStorage.getItem(BLOG_CTA_CLICK_STORAGE_KEY);

const FIRST_BLOG_VIEWED_STORAGE_KEY = 'first_blog_post_viewed';

/**
 * First-touch capture of the first blog post this visitor ever viewed -
 * deliberately a SEPARATE key from landing_page/captureLandingPage() above.
 * landing_page gets overwritten by recordThemeSelection() the moment a
 * visitor picks a gathering theme on /events, which is exactly where every
 * blog CTA sends them - so a passive "viewed this blog post" signal stored
 * only in landing_page would almost always be clobbered before checkout,
 * even though the visitor genuinely did read a blog post first. Keeping
 * this in its own key, untouched by theme selection, means "did they view
 * a blog post before subscribing" survives the realistic blog -> /events
 * -> theme -> signup journey instead of silently disappearing partway
 * through it.
 */
export const captureFirstBlogPostViewed = (slug) => {
  if (!slug) return;
  if (localStorage.getItem(FIRST_BLOG_VIEWED_STORAGE_KEY)) return;
  localStorage.setItem(FIRST_BLOG_VIEWED_STORAGE_KEY, slug);
};

export const getStoredFirstBlogPostViewed = () => localStorage.getItem(FIRST_BLOG_VIEWED_STORAGE_KEY);

const VIEWED_BLOG_SLUGS_STORAGE_KEY = 'viewed_blog_slugs';

/**
 * Full history of every distinct blog post slug this visitor has viewed on
 * this browser - unlike first_blog_post_viewed above (which only ever
 * remembers the first one), this is the complete list. Its only real job
 * is to survive as anonymous browsing history until we know who the
 * visitor is: at signup, CreateAccount.js reads it and sends it to
 * /api/signup/firstSignup, which backfills every slug into blog_post_views
 * against the new user_id - a durable, account-scoped record that outlives
 * this localStorage entry (which is device/browser-bound and can be
 * cleared). Once logged in, BlogPost.js also writes each view straight to
 * the DB directly (see standardUserApi.recordBlogView), so this array
 * matters most for the anonymous-then-signs-up case.
 */
export const recordBlogPostView = (slug) => {
  if (!slug) return;
  let slugs = [];
  try {
    slugs = JSON.parse(localStorage.getItem(VIEWED_BLOG_SLUGS_STORAGE_KEY)) || [];
  } catch {
    slugs = [];
  }
  if (!slugs.includes(slug)) {
    slugs.push(slug);
    localStorage.setItem(VIEWED_BLOG_SLUGS_STORAGE_KEY, JSON.stringify(slugs));
  }
};

export const getViewedBlogSlugs = () => {
  try {
    return JSON.parse(localStorage.getItem(VIEWED_BLOG_SLUGS_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

/**
 * Get the traffic source from URL params or referrer
 */
export const getTrafficSource = () => {
  const searchParams = new URLSearchParams(window.location.search);

  // Check for explicit user_source parameter (your existing logic)
  const userSource = searchParams.get("user_source");
  if (userSource) {
    return userSource;
  }

  // Check for common UTM parameters
  const utmSource = searchParams.get("utm_source");
  if (utmSource) {
    return utmSource;
  }

  // Check for Facebook Click ID
  if (searchParams.get("fbclid")) {
    return "facebook";
  }

  // Check for Google Click ID
  if (searchParams.get("gclid")) {
    return "google";
  }

  // Check for Microsoft/Bing Click ID
  if (searchParams.get("msclkid")) {
    return "microsoft";
  }

  // Check referrer
  const referrer = document.referrer;
  if (referrer) {
    const referrerDomain = new URL(referrer).hostname.toLowerCase();

    if (referrerDomain.includes('facebook.com') || referrerDomain.includes('fb.com')) {
      return 'facebook';
    }
    if (referrerDomain.includes('google.com')) {
      return 'google';
    }
    if (referrerDomain.includes('bing.com')) {
      return 'microsoft';
    }
    if (referrerDomain.includes('meetup.com')) {
      return 'meetup';
    }
    if (referrerDomain.includes('eventbrite.com')) {
      return 'eventbrite';
    }

    return referrerDomain;
  }

  return 'direct';
};

const USER_SOURCE_STORAGE_KEY = 'user_source';

/**
 * First-touch capture of which acquisition source (meetup, eventbrite,
 * google, meta, direct, ...) brought this visitor in - same write-once
 * pattern as captureLandingPage() above, and for the same reason: without
 * persisting it, a visitor whose first pageview lands on a theme page (not
 * the homepage) would have nothing recorded, and a later same-browser
 * visit with a different (or no) referrer would silently overwrite the
 * original attribution. Read back out at signup (CreateAccount.js) and
 * checkout (Checkout.js's metadataForCheckout) under the same
 * 'user_source' key so Postgres, Stripe metadata, and this first-touch
 * value all agree - see also the Mixpanel `traffic_source` super property
 * registered from this same value in App.js, which keeps the Mixpanel-
 * sourced funnel stages (page views, signup attempts, etc.) consistent
 * with the accounts_created/subscriptions stages that read straight from
 * Postgres/Stripe.
 */
export const captureTrafficSource = () => {
  const existing = localStorage.getItem(USER_SOURCE_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const source = getTrafficSource();
  if (source) {
    localStorage.setItem(USER_SOURCE_STORAGE_KEY, source);
  }
  return source;
};

/** The traffic source captured (if any) since this visitor's first pageview. */
export const getStoredTrafficSource = () => localStorage.getItem(USER_SOURCE_STORAGE_KEY);

const ANON_ID_STORAGE_KEY = 'anon_id';

/**
 * Permanent, write-once client-generated ID for this browser - not a real
 * user identity, just a correlation key. Used by tracking.js's
 * recordPageView (a first-party page_views row, independent of Mixpanel -
 * see models.PageView's docstring) and sent again at signup/checkout so
 * accounts_created/subscriptions can be attributed via the most recent
 * page_views row for this anon_id if this browser's own landing_page/
 * user_source localStorage entries didn't survive to conversion (cleared
 * storage, a different tab that lost them) - a more reliable fallback than
 * the previous Mixpanel-Engage-by-email join, since it doesn't depend on
 * Mixpanel having received anything either.
 */
export const getOrCreateAnonId = () => {
  const existing = localStorage.getItem(ANON_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(ANON_ID_STORAGE_KEY, id);
  return id;
};

/**
 * Get the source theme ID from multiple sources (URL param, localStorage)
 * Stores in localStorage for persistence across page navigation
 */
export const getSourceThemeId = () => {
  // 1. Check URL params first (highest priority)
  const searchParams = new URLSearchParams(window.location.search);
  const themeParam = searchParams.get("theme");

  if (themeParam) {
    // Store in localStorage for persistence
    localStorage.setItem('sourceThemeId', themeParam);
    return parseInt(themeParam);
  }

  // 2. Check localStorage (persisted from landing page or previous visit)
  const storedThemeId = localStorage.getItem('sourceThemeId');
  if (storedThemeId) {
    return parseInt(storedThemeId);
  }

  return null;
};

/**
 * Clear source theme tracking (call this after user has viewed/booked)
 */
export const clearSourceTheme = () => {
  localStorage.removeItem('sourceThemeId');
  localStorage.removeItem('sourceThemeSlug');
};

/**
 * Facebook Click ID capture/read - shared by every page that needs to
 * forward it to the backend for Meta CAPI matching (Checkout.js,
 * CreateAccount.js, EditProfile.js, MyEvents.js, Home.js). Previously
 * copy-pasted verbatim in each of those files; consolidated here so there's
 * one place to fix if the capture logic ever needs to change.
 *
 * captureFbcId() fabricates the _fbc cookie itself (rather than waiting for
 * Meta's own fbevents.js to set it) so click-id capture doesn't depend on
 * the visitor having accepted full cookie consent - fbevents.js only loads
 * under 'full' consent (see App.js), but a visitor who arrives via a Meta ad
 * and declines cookies should still be attributable if they later convert.
 */
export const captureFbcId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');

  if (fbclid) {
    const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
    document.cookie = `_fbc=${fbcValue}; path=/; max-age=7776000; secure; samesite=lax`;
    localStorage.setItem('_fbc', fbcValue);
  }
};

export const getFbcId = () => {
  const cookieMatch = document.cookie.match(/_fbc=([^;]+)/);
  if (cookieMatch) return cookieMatch[1];
  return localStorage.getItem('_fbc');
};

/**
 * Facebook Browser ID - unlike _fbc, this is set automatically by Meta's
 * own fbevents.js once the pixel loads (full consent only, see App.js), so
 * there's nothing to capture/fabricate here, just read it back out for
 * forwarding to Meta CAPI's user_data.fbp - improves Event Match Quality
 * alongside fbc/email. Read fresh (not cached) since the pixel may not have
 * loaded/set it yet at the moment a given page first calls this.
 */
export const getFbpId = () => {
  const cookieMatch = document.cookie.match(/_fbp=([^;]+)/);
  return cookieMatch ? cookieMatch[1] : null;
};

/**
 * Get source context for slots page filtering
 */
export const getSourceContext = () => {
  const themeId = getSourceThemeId();
  const source = new URLSearchParams(window.location.search).get("source");

  return {
    themeId,
    source,
    hasSourceContext: themeId !== null
  };
};